import styled from 'styled-components'
import { useEffect, useState } from 'react'
import { Route, Routes, useParams } from 'react-router-dom'
import { useGetQuery, usePostQuery } from 'hooks'
import {
  Button,
  Card,
  Carousel,
  Icon,
  Link,
  Modal,
  MultiToggle,
  PaginateTable,
  Tab,
  Tabs,
  TagManager,
  Tile,
  Title,
  Wrapper
} from 'components'

import { AddressModal, LaunchActivityModal } from './components'
import { AdminChat } from '../../games/chat'

import { getRelativeTime } from 'utils'

const Img = styled.img`
  display: inline-block;
  width: 10rem;
  height: 6rem;
  margin: 0.5rem;
  padding: 0.5rem;
`

const Game = ({ queue }) => {
  return (
    <Link to={`/admin/games/${queue.type}/${queue.id}`}>
      <Tile key={queue.id} {...queue}>
        {queue.type}
      </Tile>
    </Link>
  )
}

export const AdminPlace = () => {
  const { slug } = useParams()
  const { data: placeData, refetch } = useGetQuery({ url: `/admin/locations/${slug}` })
  const { post: updateStatus, data: statusData } = usePostQuery({ url: `/admin/locations/${slug}` })
  const { post: createAddress } = usePostQuery({ url: '/admin/addresses/' })

  const show = placeData?.location
  const [status, setStatus] = useState(null)
  const [activity, setActivity] = useState()
  const [addressModal, setAddressModal] = useState()

  useEffect(() => {
    if (show?.status && status === null) setStatus(show.status)
  }, [show])

  const changeStatus = ({ target: { value } }) => {
    const newStatus = value[1]
    setStatus(newStatus)
    updateStatus({ status: newStatus }).then(() => refetch(true))
  }

  const handleAddressResults = (results) => {
    const lat = results.geometry.location.lat()
    const lng = results.geometry.location.lng()
    setAddressModal(false)
    createAddress({
      addressable_type: 'location',
      addressable_id: show?.id,
      ...results,
      latitude: lat,
      longitude: lng,
      coords: { type: 'Point', coordinates: [lat, lng] },
      text: results.formatted_address,
      meta: results,
      tags: results.types,
      country_code: '01',
      street_address: results.formatted_address,
      city: getAddressComponent(results, 'locality'),
      country_area: getAddressComponent(results, 'administrative_area_level_1')
    }).then(() => refetch(true))
  }

  if (!show) return null

  return (
    <Card>
      <Title level="2">{show?.name}</Title>
      <Tabs>
        <Tab to={`/admin/places/${slug}`} end>
          details
        </Tab>
        <Tab to={`/admin/places/${slug}/chat`} end>
          chat
        </Tab>
      </Tabs>
      <Routes>
        <Route
          path="/"
          element={
            <>
              <section>
                {show?.addresses.map((addr) => (
                  <div key={addr.street_address}>{addr.street_address}</div>
                ))}
                {!show?.addresses.length && (
                  <Button onPress={() => setAddressModal(true)}>
                    <Icon icon="add" />
                    &nbsp;Add an address
                  </Button>
                )}
              </section>
              <section>
                <Title level="4">Host users</Title>
                {show?.user_locations?.length
                  ? show.user_locations.map((user) => (
                      <div key={user.id}>
                        <Link to={`/admin/users/${user.slug}`}>
                          {user.full_name || user.email}
                        </Link>
                        {user.UsersLocations?.role && ` — ${user.UsersLocations.role}`}
                      </div>
                    ))
                  : <div>No host users assigned.</div>}
              </section>
              <MultiToggle
                data={[
                  ['Pending', 'pending', 'warning'],
                  ['Open for business', 'active', 'success'],
                  ['Closed permanently', 'defunct', 'danger']
                ]}
                step={show?.status}
                name="status"
                onChange={changeStatus}
              />
              <LaunchActivityModal
                $show={activity}
                hide={() => setActivity(false)}
                address_id={show?.addresses[0]?.id}
              />
              <Wrapper margin="1rem 0.5rem">
                <Carousel item={show} slides={show?.media || []} update={() => refetch(true)} />
              </Wrapper>
              <TagManager
                onTagUpdate={() => refetch(true)}
                item_id={show?.id}
                item_type="location"
                tags={show?.tags}
              />
              <PaginateTable
                addItem={() => setActivity(true)}
                title="Queues"
                columns={[
                  { title: 'Type', key: 'type' },
                  { title: 'State', key: 'state' },
                  {
                    title: 'age',
                    className: 'hide-sm',
                    key: 'created_at',
                    render: (date) => getRelativeTime(date)
                  },
                  {
                    title: 'Actions',
                    key: 'id',
                    className: 'right',
                    render: (id, index, queue) => (
                      <>
                        <Link to={`/admin/games/${queue.type}/${queue.slug}`}>
                          <Icon icon="trash-can" />
                        </Link>
                        <Link to={`/admin/games/${queue.type}/${queue.slug}`}>
                          <Icon icon="pen" />
                        </Link>
                      </>
                    )
                  }
                ]}
                data={show?.addresses.map((a) => a.Queues).flat() || []}
              />
            </>
          }
        />
        <Route path="/chat" element={<AdminChat />} />
      </Routes>
      <Modal
        $show={addressModal}
        hideModal={() => setAddressModal(false)}
        title="Add an address"
      >
        <AddressModal handleResults={handleAddressResults} />
      </Modal>
    </Card>
  )
}
